// /admin/analisis-catalogo/:id
// Tabla de productos extraídos con análisis de márgenes + validación ML + bulk actions.

import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const REC_LABEL = {
  publicar_ya: { label: '🚀 Comprar YA', color: '#065f46', bg: '#d1fae5' },
  agregar: { label: '✅ Evaluar', color: '#166534', bg: '#dcfce7' },
  revisar: { label: '⚠ Revisar a mano', color: '#78350f', bg: '#fef3c7' },
  descartar: { label: '❌ Flojo', color: '#991b1b', bg: '#fee2e2' },
}

// Devuelve el análisis v10 guardado por la edge function (score, ganancia $, etc.)
const anz = (it) => it?.ml_stats?.analisis || null

// Color del score Conviene 0-100
function scoreColor(s) {
  if (s == null) return { color: '#94a3b8', bg: '#f1f5f9' }
  if (s >= 70) return { color: '#065f46', bg: '#d1fae5' }
  if (s >= 55) return { color: '#166534', bg: '#dcfce7' }
  if (s >= 40) return { color: '#78350f', bg: '#fef3c7' }
  return { color: '#991b1b', bg: '#fee2e2' }
}

const CATEGORIA_LABEL = {
  lavado_ropa: '🧺 Lavado de ropa',
  vajilla: '🍽 Vajilla',
  limpieza_general: '🧽 Limpieza general',
  desinfectantes: '🦠 Desinfectantes',
  papel_higienico: '🧻 Papel higiénico',
  papel_cocina: '📄 Papel cocina',
  bolsas: '🗑 Bolsas',
  jabones: '🧼 Jabones',
  accesorios: '🧰 Accesorios',
  otros: '📦 Otros',
}

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')

export default function AnalisisCatalogoDetalle() {
  const { id } = useParams()
  const nav = useNavigate()
  const [analisis, setAnalisis] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [seleccionados, setSeleccionados] = useState(new Set())
  const [filtroRec, setFiltroRec] = useState('')
  const [filtroSinDatos, setFiltroSinDatos] = useState(false)
  const [filtroCat, setFiltroCat] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [ejecutando, setEjecutando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [extInstalada, setExtInstalada] = useState(false)
  const [progreso, setProgreso] = useState(null) // { indice, total, nombre, portal, phase }
  const [borradores, setBorradores] = useState({}) // { [id]: { precio, link } } — edición manual por fila
  const [guardandoManual, setGuardandoManual] = useState(() => new Set())
  const [filtroVerificados, setFiltroVerificados] = useState(false)

  useEffect(() => { cargar() }, [id])

  // Detectar extensión Chrome "Limpack — Precios"
  useEffect(() => {
    function onMessage(e) {
      if (e.source !== window) return
      if (e.data?.type === 'LIMPACK_EXTENSION_READY') setExtInstalada(true)
    }
    window.addEventListener('message', onMessage)
    // Ping repetido: el content script puede inyectarse después del mount
    const pings = [0, 300, 800, 2000, 4000].map(delay =>
      setTimeout(() => window.postMessage({ type: 'LIMPACK_EXTENSION_CHECK' }, '*'), delay)
    )
    return () => {
      window.removeEventListener('message', onMessage)
      pings.forEach(clearTimeout)
    }
  }, [])

  async function cargar() {
    setLoading(true)
    const [{ data: a }, { data: its }] = await Promise.all([
      supabase.from('analisis_catalogo').select('*').eq('id', id).single(),
      supabase.from('analisis_catalogo_items').select('*').eq('analisis_id', id).order('orden'),
    ])
    // Enriquecer los items linkeados con datos actuales del producto Limpack
    // (ml_precio real, ml_status, ml_item_id) para poder comparar sugerencias vs realidad publicada.
    const productoIds = [...new Set((its || []).map(i => i.producto_id).filter(Boolean))]
    let prodMap = {}
    if (productoIds.length > 0) {
      const { data: prods } = await supabase.from('productos')
        .select('id, ml_precio, ml_status, ml_item_id, precio_publico, costo_unitario')
        .in('id', productoIds)
      prodMap = Object.fromEntries((prods || []).map(p => [p.id, p]))
    }
    const enriched = (its || []).map(i => ({ ...i, _producto: i.producto_id ? prodMap[i.producto_id] : null }))
    setAnalisis(a)
    setItems(enriched)
    // Semilla de borradores para los inputs de precio/link manuales.
    setBorradores(Object.fromEntries(enriched.map(i => [i.id, {
      precio: i.precio_ref_manual != null ? String(i.precio_ref_manual) : '',
      link: i.link_ref_manual || '',
    }])))
    setLoading(false)
  }

  // Marcar/desmarcar "Ok" (revisado a mano) sin recalcular. Optimista.
  async function toggleRevisado(it) {
    const nuevo = !it.revisado_ok
    setItems(prev => prev.map(x => x.id === it.id ? { ...x, revisado_ok: nuevo } : x))
    const { error } = await supabase.from('analisis_catalogo_items')
      .update({ revisado_ok: nuevo, verificado_at: nuevo ? new Date().toISOString() : null })
      .eq('id', it.id)
    if (error) {
      setItems(prev => prev.map(x => x.id === it.id ? { ...x, revisado_ok: it.revisado_ok } : x))
      setMsg({ tipo: 'err', txt: 'No se pudo guardar el Ok: ' + error.message })
    }
  }

  // Guardar precio/link manual. Si hay precio, el edge recalcula rentabilidad con la MISMA fórmula.
  async function guardarManual(it) {
    const b = borradores[it.id] || {}
    const precioNum = b.precio === '' || b.precio == null ? null : Number(b.precio)
    const link = (b.link || '').trim() || null
    if (precioNum != null && (isNaN(precioNum) || precioNum < 0)) {
      setMsg({ tipo: 'err', txt: 'Precio inválido' }); return
    }
    // Nada cambió respecto a lo guardado → no hacemos nada.
    const mismoPrecio = (precioNum ?? null) === (it.precio_ref_manual ?? null)
    const mismoLink = (link ?? null) === (it.link_ref_manual ?? null)
    if (mismoPrecio && mismoLink) return

    setGuardandoManual(prev => new Set(prev).add(it.id))
    try {
      const { error } = await supabase.functions.invoke('guardar-precios-scraping', {
        body: { item_id: it.id, manual: { precio_ref_manual: precioNum, link_ref_manual: link } },
      })
      if (error) throw error
      await cargar()
      setMsg({ tipo: 'ok', txt: precioNum ? `✓ ${it.marca} ${it.nombre}: rentabilidad recalculada a $${precioNum}` : '✓ Guardado' })
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error guardando manual: ' + (e?.message || e) })
    } finally {
      setGuardandoManual(prev => { const s = new Set(prev); s.delete(it.id); return s })
    }
  }

  const itemsFiltrados = useMemo(() => {
    let f = items
    if (filtroRec) f = f.filter(i => i.recomendacion === filtroRec)
    if (filtroSinDatos) f = f.filter(i => !(i.ml_publicaciones_activas > 0) && !i.revisado_ok)
    if (filtroVerificados) f = f.filter(i => i.revisado_ok)
    if (filtroCat) f = f.filter(i => i.categoria_sugerida === filtroCat)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(i =>
        (i.nombre || '').toLowerCase().includes(q) ||
        (i.marca || '').toLowerCase().includes(q) ||
        (i.presentacion || '').toLowerCase().includes(q)
      )
    }
    // Orden por defecto: score Conviene desc (los que más convienen arriba);
    // los sin datos quedan al final. Empate → orden original del catálogo.
    return [...f].sort((a, b) => {
      const sa = anz(a)?.score, sb = anz(b)?.score
      if (sa == null && sb == null) return (a.orden || 0) - (b.orden || 0)
      if (sa == null) return 1
      if (sb == null) return -1
      return sb - sa
    })
  }, [items, filtroRec, filtroSinDatos, filtroVerificados, filtroCat, busqueda])

  const conteos = useMemo(() => {
    const c = { publicar_ya: 0, agregar: 0, revisar: 0, descartar: 0, sin_rec: 0, sin_datos: 0, verificados: 0 }
    for (const it of items) {
      const sinDatos = !(it.ml_publicaciones_activas > 0) && !it.revisado_ok
      if (sinDatos) c.sin_datos++
      if (it.revisado_ok) c.verificados++
      if (!it.recomendacion) c.sin_rec++
      else c[it.recomendacion] = (c[it.recomendacion] || 0) + 1
    }
    // "Revisar a mano" con datos = total revisar − los que no tienen mercado.
    c.revisar_con_datos = Math.max(0, c.revisar - c.sin_datos)
    return c
  }, [items])

  function toggleSel(id) {
    const s = new Set(seleccionados)
    if (s.has(id)) s.delete(id); else s.add(id)
    setSeleccionados(s)
  }

  function toggleTodos() {
    if (seleccionados.size === itemsFiltrados.length) setSeleccionados(new Set())
    else setSeleccionados(new Set(itemsFiltrados.map(i => i.id)))
  }

  async function agregarAProductos() {
    if (seleccionados.size === 0) { setMsg({ tipo: 'err', txt: 'Elegí al menos un producto' }); return }
    const seleccionadosArr = items.filter(i => seleccionados.has(i.id) && !i.ya_existe)
    if (seleccionadosArr.length === 0) { setMsg({ tipo: 'err', txt: 'Los seleccionados ya existen en catálogo' }); return }
    if (!confirm(`¿Agregar ${seleccionadosArr.length} productos NUEVOS al catálogo Limpack?\n\nSe crean con: precio_publico = precio_limpack_sugerido, activo=true, sin foto.`)) return

    setEjecutando(true)
    try {
      const nuevos = seleccionadosArr.map(it => ({
        nombre: it.nombre,
        marca: it.marca,
        presentacion: it.presentacion,
        precio_publico: it.precio_limpack_sugerido,
        precio_mercado: it.ml_mediana || it.precio_limpack_sugerido,
        activo: true,
        iva_pct: 22,
      }))
      const { data: creados, error } = await supabase.from('productos').insert(nuevos).select('id')
      if (error) throw error

      // Marcar items como agregados
      await supabase.from('analisis_catalogo_items')
        .update({ agregado_a_productos: true })
        .in('id', seleccionadosArr.map(i => i.id))

      setMsg({ tipo: 'ok', txt: `✅ ${creados.length} productos agregados al catálogo` })
      setSeleccionados(new Set())
      cargar()
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error: ' + e.message })
    } finally {
      setEjecutando(false)
    }
  }

  async function setearPrecioML() {
    if (seleccionados.size === 0) { setMsg({ tipo: 'err', txt: 'Elegí al menos un producto' }); return }
    const seleccionadosArr = items.filter(i => seleccionados.has(i.id) && i.producto_id && i.precio_ml_sugerido)
    if (seleccionadosArr.length === 0) { setMsg({ tipo: 'err', txt: 'Solo productos ya en catálogo pueden setear precio ML' }); return }
    if (!confirm(`¿Setear ml_precio para ${seleccionadosArr.length} productos existentes?`)) return

    setEjecutando(true)
    try {
      let done = 0
      for (const it of seleccionadosArr) {
        await supabase.from('productos').update({ ml_precio: it.precio_ml_sugerido }).eq('id', it.producto_id)
        done++
      }
      await supabase.from('analisis_catalogo_items')
        .update({ agregado_a_ml: true })
        .in('id', seleccionadosArr.map(i => i.id))
      setMsg({ tipo: 'ok', txt: `✅ Precio ML seteado en ${done} productos` })
      setSeleccionados(new Set())
      cargar()
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error: ' + e.message })
    } finally {
      setEjecutando(false)
    }
  }

  async function validarEnML() {
    if (seleccionados.size === 0) { setMsg({ tipo: 'err', txt: 'Elegí productos para validar' }); return }
    if (!extInstalada) {
      setMsg({ tipo: 'err', txt: 'La extensión "Limpack — Precios" no está instalada. Ver limpack-intranet/extension-precios/README.md' })
      return
    }
    const seleccionadosArr = items.filter(i => seleccionados.has(i.id))
    const yaValidados = seleccionadosArr.filter(i => i.ml_stats).length
    const sinValidar = seleccionadosArr // procesamos TODOS: si tienen datos viejos, sobrescribe
    const segundos = Math.ceil(sinValidar.length * 8)
    const advertencia = yaValidados > 0
      ? `\n\n⚠️ ${yaValidados} ya tenían datos previos — se sobrescribirán con los nuevos.`
      : ''
    if (!confirm(`¿Buscar precios en MercadoLibre para ${sinValidar.length} productos?\n\nLa extensión abre pestañas ocultas en tu navegador y scrapea el DOM. ~${segundos}s total. Sin costo.${advertencia}`)) return

    setEjecutando(true)
    setProgreso({ indice: 0, total: sinValidar.length, portal: null, phase: null })
    setMsg({ tipo: 'info', txt: `🔍 Buscando precios de ${sinValidar.length} productos vía extensión...` })

    // Trackeamos los guardados pendientes para esperarlos antes de cargar()
    const guardadosPendientes = new Set()

    // Handlers de mensajes de la extensión
    async function onItemListo(e) {
      if (e.source !== window) return
      const d = e.data || {}

      if (d.type === 'LIMPACK_ITEM_START') {
        setProgreso({ indice: d.indice, total: d.total, nombre: d.nombre })
      }

      if (d.type === 'LIMPACK_PROGRESS') {
        setProgreso(p => p ? { ...p, portal: d.portal, phase: d.phase, count: d.count } : null)
      }

      if (d.type === 'LIMPACK_ITEM_LISTO' && d.resultado) {
        // Guardar en Supabase incremental. Registramos el promise para esperarlo
        // antes del cargar() final — sino la tabla se refresca antes de que
        // guardar-precios-scraping haya persistido y quedan todos en null.
        const p = supabase.functions.invoke('guardar-precios-scraping', {
          body: { item_id: d.item_id, resultado: d.resultado },
        }).catch(err => console.error('guardar-precios-scraping fallo:', err))
        guardadosPendientes.add(p)
        p.finally(() => guardadosPendientes.delete(p))
      }

      if (d.type === 'LIMPACK_BLOQUEADO') {
        // ML sirvió el muro de captcha y frenó el escaneo. NO se guardaron ceros
        // falsos: los que faltan quedan como estaban. Este msg queda fijo aunque
        // llegue LIMPACK_BUSQUEDA_LISTA después.
        setMsg({ tipo: 'err', txt: `🚧 MercadoLibre pidió captcha y frenó el escaneo (procesados ${d.procesados}/${d.total}, quedaron ${d.restantes}). Abrí mercadolibre.com.uy en otra pestaña, resolvé el "no soy un robot", esperá unos minutos y reintentá SOLO los que quedaron sin datos, en tandas de 50-60. No se guardó nada falso.` })
      }

      if (d.type === 'LIMPACK_BUSQUEDA_LISTA') {
        window.removeEventListener('message', onItemListo)
        // Esperar que todos los guardar-precios-scraping terminen antes de
        // refrescar la tabla desde DB
        await Promise.all([...guardadosPendientes])
        if (!d.bloqueado) {
          setMsg({ tipo: 'ok', txt: `✅ ${d.resultados?.length || 0} productos validados con datos reales de mercado` })
        }
        setSeleccionados(new Set())
        setProgreso(null)
        setEjecutando(false)
        cargar()
      }

      if (d.type === 'LIMPACK_BUSQUEDA_ERROR') {
        window.removeEventListener('message', onItemListo)
        setMsg({ tipo: 'err', txt: 'Error en extensión: ' + d.error })
        setProgreso(null)
        setEjecutando(false)
      }
    }
    window.addEventListener('message', onItemListo)

    // Disparar la búsqueda
    window.postMessage({
      type: 'LIMPACK_BUSCAR_PRECIOS',
      payload: {
        productos: sinValidar.map(i => ({
          item_id: i.id,
          marca: i.marca,
          nombre: i.nombre,
          presentacion: i.presentacion,
        })),
        portales: ['MercadoLibre'], // por ahora solo ML, Prolimpio/Suchina/Divino en fase 2
      },
    }, '*')
  }

  async function descartarSeleccionados() {
    if (seleccionados.size === 0) return
    if (!confirm(`¿Descartar ${seleccionados.size} productos? Los saco de esta lista pero quedan registrados.`)) return
    setEjecutando(true)
    try {
      await supabase.from('analisis_catalogo_items')
        .update({ descartado: true })
        .in('id', Array.from(seleccionados))
      setMsg({ tipo: 'ok', txt: `${seleccionados.size} descartados` })
      setSeleccionados(new Set())
      cargar()
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error: ' + e.message })
    } finally {
      setEjecutando(false)
    }
  }

  if (loading) return <AdminLayout title="Análisis"><div style={{ padding: 40, textAlign: 'center' }}>Cargando...</div></AdminLayout>
  if (!analisis) return <AdminLayout title="Análisis"><div style={{ padding: 24 }}>Análisis no encontrado</div></AdminLayout>

  return (
    <AdminLayout title={`Análisis · ${analisis.proveedor}`}>
      <div style={{ padding: 24 }}>
        <button onClick={() => nav('/admin/analisis-catalogo')} style={{ background: 'transparent', border: 0, color: '#0ea5e9', cursor: 'pointer', fontSize: 12, marginBottom: 12 }}>← Volver</button>

        <h1 style={{ margin: 0, color: '#1a3461', fontSize: 22 }}>{analisis.proveedor} · {new Date(analisis.created_at).toLocaleDateString('es-UY')}</h1>
        <p style={{ color: '#64748b', marginTop: 6, fontSize: 13 }}>
          {analisis.archivo_nombre} · {analisis.total_productos} productos extraídos · Costo API: ${Number(analisis.costo_api_estimado || 0).toFixed(3)} USD
        </p>

        {/* Estado extensión Chrome */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          marginTop: 8, padding: '4px 10px',
          background: extInstalada ? '#f0fdf4' : '#fef3c7',
          border: `1px solid ${extInstalada ? '#86efac' : '#fcd34d'}`,
          borderRadius: 999, fontSize: 11, fontWeight: 600,
          color: extInstalada ? '#166534' : '#78350f',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: extInstalada ? '#22c55e' : '#f59e0b',
          }} />
          {extInstalada ? 'Extensión Precios conectada' : 'Extensión Chrome "Limpack — Precios" no instalada'}
        </div>

        {/* Progreso de búsqueda */}
        {progreso && (
          <div style={{ marginTop: 10, padding: 10, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: '#1e40af' }}>
              🔍 Producto {progreso.indice}/{progreso.total}: {progreso.nombre}
            </div>
            {progreso.portal && (
              <div style={{ color: '#64748b', marginTop: 4 }}>
                {progreso.portal}: {
                  progreso.phase === 'buscando' ? 'abriendo pestaña...'
                  : progreso.phase === 'listo' ? `${progreso.count} publicaciones ✓`
                  : progreso.phase === 'reintentando' ? 'vacío, reintentando...'
                  : progreso.phase === 'enfriando' ? 'enfriando (varios vacíos seguidos)...'
                  : progreso.phase === 'enfriando_bloqueo' ? '🚧 ML pidió captcha, enfriando...'
                  : progreso.phase === 'bloqueado' ? '🚧 bloqueado'
                  : progreso.phase
                }
              </div>
            )}
            <div style={{ marginTop: 6, height: 4, background: '#dbeafe', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                width: `${(progreso.indice / progreso.total) * 100}%`,
                height: '100%', background: '#3b82f6', transition: 'width 0.3s',
              }} />
            </div>
          </div>
        )}

        {/* Cards de resumen */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, margin: '20px 0' }}>
          {Object.entries(REC_LABEL).map(([k, v]) => {
            const activo = filtroRec === k && !filtroSinDatos
            // "Revisar a mano" muestra solo los que tienen mercado; los sin datos van aparte.
            const num = k === 'revisar' ? conteos.revisar_con_datos : (conteos[k] || 0)
            return (
              <button
                key={k}
                onClick={() => { setFiltroSinDatos(false); setFiltroVerificados(false); setFiltroRec(filtroRec === k ? '' : k) }}
                style={{
                  padding: 12, background: activo ? v.bg : 'white',
                  border: `2px solid ${activo ? v.color : '#e2e8f0'}`,
                  borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 11, color: v.color, fontWeight: 700, textTransform: 'uppercase' }}>{v.label}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: v.color, marginTop: 4 }}>{num}</div>
              </button>
            )
          })}
          {/* Sin datos: el scraper no trajo mercado. NO es "malo": es "no sabemos". */}
          <button
            onClick={() => { setFiltroRec(''); setFiltroVerificados(false); setFiltroSinDatos(v => !v) }}
            title="El scraper no encontró publicaciones en ML. No decide: hay que rescanear o buscar a mano."
            style={{
              padding: 12, background: filtroSinDatos ? '#e2e8f0' : 'white',
              border: `2px solid ${filtroSinDatos ? '#475569' : '#e2e8f0'}`,
              borderRadius: 10, cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' }}>🕳 Sin datos</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#475569', marginTop: 4 }}>{conteos.sin_datos}</div>
          </button>
          {/* Verificados a mano por Nico (revisado_ok) */}
          <button
            onClick={() => { setFiltroRec(''); setFiltroSinDatos(false); setFiltroVerificados(v => !v) }}
            title="Items que marcaste Ok / verificaste a mano. Los que tienen precio manual traen la rentabilidad recalculada."
            style={{
              padding: 12, background: filtroVerificados ? '#d1fae5' : 'white',
              border: `2px solid ${filtroVerificados ? '#059669' : '#e2e8f0'}`,
              borderRadius: 10, cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 11, color: '#059669', fontWeight: 700, textTransform: 'uppercase' }}>✓ Verificados</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#059669', marginTop: 4 }}>{conteos.verificados}</div>
          </button>
        </div>

        {/* Filtros + búsqueda */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar por nombre, marca o presentación..."
            style={{ flex: 1, minWidth: 200, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
          />
          <select value={filtroCat} onChange={e => setFiltroCat(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}>
            <option value="">Todas las categorías</option>
            {Object.entries(CATEGORIA_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {/* Bulk actions */}
        {seleccionados.size > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, padding: 10, background: '#f0f9ff', border: '1px solid #7dd3fc', borderRadius: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#075985' }}>{seleccionados.size} seleccionados</span>
            <button onClick={validarEnML} disabled={ejecutando} style={{ padding: '6px 12px', background: '#f59e0b', color: 'white', border: 0, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              🔍 Validar precio en ML
            </button>
            <button onClick={agregarAProductos} disabled={ejecutando} style={{ padding: '6px 12px', background: '#16a34a', color: 'white', border: 0, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              📦 Agregar a Limpack
            </button>
            <button onClick={setearPrecioML} disabled={ejecutando} style={{ padding: '6px 12px', background: '#0ea5e9', color: 'white', border: 0, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              🛒 Setear precio ML
            </button>
            <button onClick={descartarSeleccionados} disabled={ejecutando} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 0, borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              🗑 Descartar
            </button>
            <button onClick={() => setSeleccionados(new Set())} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
              Deseleccionar
            </button>
          </div>
        )}

        {msg && (
          <div style={{ padding: 10, marginBottom: 12, background: msg.tipo === 'ok' ? '#d1fae5' : '#fee2e2', border: `1px solid ${msg.tipo === 'ok' ? '#86efac' : '#fca5a5'}`, borderRadius: 6, color: msg.tipo === 'ok' ? '#065f46' : '#991b1b', fontSize: 12 }}>
            {msg.txt}
          </div>
        )}

        {/* Tabla */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontSize: 10, textTransform: 'uppercase', color: '#64748b' }}>
                <th style={{ padding: 8, width: 30 }}>
                  <input
                    type="checkbox"
                    checked={seleccionados.size === itemsFiltrados.length && itemsFiltrados.length > 0}
                    onChange={toggleTodos}
                  />
                </th>
                <th style={{ padding: 8, textAlign: 'left' }}>Producto</th>
                <th style={{ padding: 8, textAlign: 'center' }} title="Índice Conviene 0-100: mezcla rentabilidad, ganancia $ y defensibilidad. Ordena la tabla.">Conviene</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Costo</th>
                <th style={{ padding: 8, textAlign: 'right' }}>ML sug.</th>
                <th style={{ padding: 8, textAlign: 'right' }} title="Precio real al que está publicado en nuestra cuenta ML">Nuestro ML</th>
                <th style={{ padding: 8, textAlign: 'right' }} title="Precios de ese producto en otras publicaciones del mercado (scraping)">Precio ML mercado</th>
                <th style={{ padding: 8, textAlign: 'right' }} title="Ganancia en $ por unidad al precio de entrada, ya sin comisión ni cargo fijo">Ganancia $</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Margen</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Veredicto</th>
                <th style={{ padding: 8, textAlign: 'left' }} title="Marcá Ok y cargá el precio real de ML (más barato). Con precio, recalculo la rentabilidad con tu misma fórmula. El link es opcional.">✓ Verificación</th>
              </tr>
            </thead>
            <tbody>
              {itemsFiltrados.map(it => {
                const sinDatos = !(it.ml_publicaciones_activas > 0) && !it.revisado_ok
                const rec = sinDatos
                  ? { label: '🕳 Sin datos', color: '#475569', bg: '#e2e8f0' }
                  : (REC_LABEL[it.recomendacion] || { label: '—', color: '#64748b', bg: '#f1f5f9' })
                const esManual = it.ml_stats?.fuente_veredicto === 'manual'
                const guardando = guardandoManual.has(it.id)
                const bo = borradores[it.id] || { precio: '', link: '' }
                return (
                  <tr key={it.id} style={{ borderBottom: '1px solid #f1f5f9', background: it.descartado ? '#fef2f2' : it.agregado_a_productos ? '#f0fdf4' : 'white' }}>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <input type="checkbox" checked={seleccionados.has(it.id)} onChange={() => toggleSel(it.id)} />
                    </td>
                    <td style={{ padding: 8 }}>
                      <div style={{ fontWeight: 700, color: '#1a3461' }}>{it.marca} · {it.nombre}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {it.presentacion} · {it.categoria_sugerida ? CATEGORIA_LABEL[it.categoria_sugerida] : ''}
                        {it.ya_existe && <span style={{ marginLeft: 6, background: '#fef3c7', color: '#78350f', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>YA EN CATÁLOGO</span>}
                        {it.agregado_a_productos && <span style={{ marginLeft: 6, background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>✓ AGREGADO</span>}
                        {it.observaciones_ia && <div style={{ marginTop: 2, fontStyle: 'italic' }}>{it.observaciones_ia}</div>}
                      </div>
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {(() => {
                        const a = anz(it); const s = a?.score
                        if (s == null) return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                        const c = scoreColor(s); const comp = a?.score_componentes
                        return (
                          <div title={comp ? `Rentabilidad ${comp.rent} · Ganancia ${comp.ganancia} · Defensa ${comp.defensa}` : ''}
                            style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 46, padding: '3px 6px', background: c.bg, borderRadius: 8 }}>
                            <span style={{ fontSize: 18, fontWeight: 900, color: c.color, lineHeight: 1 }}>{s}</span>
                            {comp && <span style={{ fontSize: 8, color: c.color, marginTop: 2 }}>R{comp.rent}·G{comp.ganancia}·D{comp.defensa}</span>}
                          </div>
                        )
                      })()}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#64748b' }}>{fmt(it.costo_ciiva)}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>{fmt(it.precio_ml_sugerido)}</td>
                    {/* Nuestro ML — precio real de nuestra publicación en MELI */}
                    <td style={{ padding: 8, textAlign: 'right', minWidth: 100 }}>
                      {(() => {
                        const prod = it._producto
                        if (!prod?.ml_item_id) {
                          return <span style={{ fontSize: 10, color: '#94a3b8' }}>no publicado</span>
                        }
                        const nuestroML = Number(prod.ml_precio || 0)
                        const sug = Number(it.precio_ml_sugerido || 0)
                        const diff = sug > 0 ? ((nuestroML / sug) - 1) * 100 : null
                        const color = !diff ? '#1a3461' : diff >= -5 && diff <= 5 ? '#0369a1' : diff > 5 ? '#166534' : '#dc2626'
                        return (
                          <>
                            <div style={{ fontSize: 14, fontWeight: 700, color }}>{fmt(nuestroML)}</div>
                            {diff !== null && (
                              <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>
                                {diff >= 0 ? '+' : ''}{diff.toFixed(0)}% vs sug
                              </div>
                            )}
                            <div style={{ fontSize: 9, color: prod.ml_status === 'active' ? '#16a34a' : '#f59e0b', fontWeight: 700, marginTop: 1 }}>
                              {prod.ml_status === 'active' ? '● LIVE' : (prod.ml_status || '').toUpperCase()}
                            </div>
                          </>
                        )
                      })()}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right', minWidth: 190 }}>
                      {(() => {
                        // "Comparo contra": el match exacto (mismo producto) si existe; si no, el más barato válido.
                        const match = it.ml_stats?.match_exacto
                        const barato = it.ml_stats?.mas_barato
                        const ref = (match?.precio ? match : null) || (barato?.precio ? barato : null)
                        if (ref?.precio) {
                          const esMatch = !!(match?.precio)
                          return (
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                              {ref.imagen_url && (
                                <a href={ref.url || '#'} target="_blank" rel="noopener" title={ref.titulo}>
                                  <img src={ref.imagen_url} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', flexShrink: 0 }} />
                                </a>
                              )}
                              <div style={{ minWidth: 120 }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#0369a1' }}>{fmt(ref.precio)}</div>
                                <div style={{ fontSize: 10, color: esMatch ? '#0369a1' : '#64748b', marginTop: 1, fontWeight: 700 }}>
                                  {esMatch ? '🎯 Match exacto' : '💲 Más barato'}
                                </div>
                                {ref.lleva_regalo && (
                                  <div style={{ display: 'inline-block', marginTop: 3, background: '#fef3c7', color: '#78350f', padding: '1px 7px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                                    🎁 Con regalo/combo — no comparable pelado
                                  </div>
                                )}
                                {ref.envio_gratis && (
                                  <div style={{ fontSize: 9, color: '#16a34a', fontWeight: 700, marginTop: 2 }}>envío gratis</div>
                                )}
                                {ref.url && (
                                  <a href={ref.url} target="_blank" rel="noopener" title={ref.titulo} style={{ fontSize: 10, color: '#0ea5e9', textDecoration: 'none', display: 'block', marginTop: 3, fontWeight: 700 }}>
                                    Comparo contra ↗
                                  </a>
                                )}
                                {it.ml_mediana && (
                                  <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
                                    mediana {fmt(it.ml_mediana)} · {it.ml_publicaciones_activas} publ.
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        }
                        if (it.ml_mediana) {
                          return (
                            <>
                              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a3461' }}>{fmt(it.ml_mediana)}</div>
                              <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>mediana</div>
                              <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                                min <b style={{ color: '#16a34a' }}>{fmt(it.ml_min)}</b> · max <b style={{ color: '#dc2626' }}>{fmt(it.ml_max)}</b>
                              </div>
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>{it.ml_publicaciones_activas} publ.</div>
                            </>
                          )
                        }
                        return <span style={{ color: '#cbd5e1' }}>—</span>
                      })()}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>
                      {(() => {
                        const g = anz(it)?.ganancia_pesos
                        if (g == null) return <span style={{ color: '#cbd5e1' }}>—</span>
                        return <span style={{ color: g >= 63 ? '#166534' : g > 0 ? '#78350f' : '#991b1b' }}>{fmt(g)}</span>
                      })()}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 700 }}>{it.margen_ml_pct != null ? it.margen_ml_pct + '%' : '—'}</div>
                    </td>
                    <td style={{ padding: 8, maxWidth: 220 }}>
                      <span style={{ display: 'inline-block', padding: '3px 8px', background: rec.bg, color: rec.color, borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                        {rec.label}
                      </span>
                      {esManual && (
                        <span style={{ marginLeft: 6, display: 'inline-block', padding: '2px 7px', background: '#dbeafe', color: '#1e40af', borderRadius: 999, fontSize: 9, fontWeight: 700 }}>✓ a mano</span>
                      )}
                      {it.recomendacion_razon && (
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>{it.recomendacion_razon}</div>
                      )}
                    </td>
                    {/* Verificación manual: Ok + precio real + link. Con precio, el edge recalcula rentabilidad. */}
                    <td style={{ padding: 8, minWidth: 180, background: it.revisado_ok ? '#f0fdf4' : undefined }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: it.revisado_ok ? '#059669' : '#64748b', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!it.revisado_ok} onChange={() => toggleRevisado(it)} />
                        Ok {it.revisado_ok ? '' : ''}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>$</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          placeholder="precio real ML"
                          value={bo.precio}
                          disabled={guardando}
                          onChange={e => setBorradores(prev => ({ ...prev, [it.id]: { ...bo, precio: e.target.value } }))}
                          onBlur={() => guardarManual(it)}
                          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                          style={{ width: 96, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                        />
                      </div>
                      <input
                        type="url"
                        placeholder="🔗 link (opcional)"
                        value={bo.link}
                        disabled={guardando}
                        onChange={e => setBorradores(prev => ({ ...prev, [it.id]: { ...bo, link: e.target.value } }))}
                        onBlur={() => guardarManual(it)}
                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                        style={{ width: 150, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, marginTop: 4 }}
                      />
                      {guardando && <div style={{ fontSize: 10, color: '#0ea5e9', marginTop: 4, fontWeight: 700 }}>recalculando…</div>}
                      {!guardando && it.precio_ref_manual != null && (
                        <div style={{ fontSize: 10, color: '#059669', marginTop: 4, fontWeight: 700 }}>
                          ✓ recalculado a ${Math.round(Number(it.precio_ref_manual)).toLocaleString('es-UY')}
                          {it.link_ref_manual && <> · <a href={it.link_ref_manual} target="_blank" rel="noopener" style={{ color: '#0ea5e9', textDecoration: 'none' }}>ver ↗</a></>}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
              {itemsFiltrados.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>Sin resultados con los filtros actuales</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  )
}
